import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

function formatRange(startsAt, endsAt) {
  try {
    const a = new Date(startsAt + "T12:00:00");
    const b = new Date(endsAt + "T12:00:00");
    const opts = { month: "short", day: "numeric" };
    return `${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, opts)}`;
  } catch {
    return `${startsAt} – ${endsAt}`;
  }
}

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function findCurrentSprintIndex(sprints) {
  const today = todayKey();
  return sprints.findIndex(
    (s) =>
      (s.starts_at || s.startsAt) <= today &&
      (s.ends_at || s.endsAt) >= today
  );
}

/**
 * Prev/next sprint scrubber + All / This sprint toggle.
 */
export default function SprintScrubber({
  sprints = [],
  selectedIndex = 0,
  onSelectIndex,
  filterMode = "all", // 'all' | 'sprint'
  onFilterModeChange,
  onStartSprint,
  onRenameSprint,
  canManage = false,
  starting = false,
  className,
}) {
  const hasSprints = sprints.length > 0;
  const sprint = hasSprints ? sprints[selectedIndex] : null;
  const name = sprint?.name;
  const range = sprint
    ? formatRange(sprint.starts_at || sprint.startsAt, sprint.ends_at || sprint.endsAt)
    : null;
  const currentIdx = findCurrentSprintIndex(sprints);

  if (!hasSprints) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-3 py-2",
          className
        )}
      >
        <p className="text-sm text-muted-foreground flex-1 min-w-0">
          No sprints yet. Set how long sprints run, then start Sprint 1.
        </p>
        {canManage ? (
          <Button
            type="button"
            size="sm"
            disabled={starting}
            onClick={onStartSprint}
          >
            {starting ? "Starting…" : "Start Sprint 1"}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-2 py-2",
        className
      )}
    >
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={selectedIndex <= 0}
          onClick={() => onSelectIndex?.(selectedIndex - 1)}
          aria-label="Previous sprint"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 text-center flex-1 px-1">
          <p className="text-sm font-medium text-foreground truncate">{name}</p>
          <p className="text-[11px] text-muted-foreground truncate">{range}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={selectedIndex >= sprints.length - 1}
          onClick={() => onSelectIndex?.(selectedIndex + 1)}
          aria-label="Next sprint"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {currentIdx >= 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={selectedIndex === currentIdx}
            onClick={() => onSelectIndex?.(currentIdx)}
          >
            Current
          </Button>
        ) : null}
        <div
          className="inline-flex rounded-md border border-border p-0.5"
          role="tablist"
          aria-label="Board sprint filter"
        >
          <button
            type="button"
            role="tab"
            aria-selected={filterMode === "all"}
            title="Show all cards on the board"
            onClick={() => onFilterModeChange?.("all")}
            className={cn(
              "px-2 py-1 text-xs rounded-sm transition-colors",
              filterMode === "all"
                ? "bg-muted text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            All cards
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filterMode === "sprint"}
            title="Show only cards assigned to this sprint (includes Done)"
            onClick={() => onFilterModeChange?.("sprint")}
            className={cn(
              "px-2 py-1 text-xs rounded-sm transition-colors",
              filterMode === "sprint"
                ? "bg-muted text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            This sprint
          </button>
        </div>
        {canManage ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={onRenameSprint}
            >
              Rename
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={starting}
              onClick={onStartSprint}
            >
              {starting ? "…" : "New sprint"}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
